import { useState } from 'react'
import { filterOptions, PickerInput, type PickerOption } from './picker-input'
import { pickedFrom, useRegistrySearch, type PickedProduct } from './product-picker'
import type { StockItem } from './types'

/** Where an applied product's record goes: a pesticide to Form H1, a fertilizer (or other input) to H2. */
export type InputKind = 'pesticide' | 'fertilizer'

/** What picking a product records on the entry. Typing instead keeps only the name, and no kind. */
export type PickedInput = { productName: string; kind: InputKind | ''; stockItemId: string } & PickedProduct

const STOCK_KIND: Record<StockItem['category'], InputKind | ''> = { PESTICIDE: 'pesticide', FERTILIZER: 'fertilizer', OTHER: '' }
const STOCK_LABEL: Record<StockItem['category'], string> = { PESTICIDE: 'Pesticide', FERTILIZER: 'Fertilizer', OTHER: 'Other' }

/**
 * The name box for anything applied to a crop: the farm's own stock first (any category), then
 * fertilizers and blends already used here, then Health Canada's pesticide registry. Picking one
 * says whether it is a pesticide or a fertilizer, so its record goes to the right form; a typed name
 * leaves that to the grower. Editing the text after picking unlinks it. Wrap it in a Catalyst `Field`.
 */
export function InputPicker({
  name,
  picked,
  stock,
  pastBlends,
  onName,
  onPick,
}: {
  name: string
  /** What the entry is linked to now: a stock item and/or a registered product. */
  picked: { stockItemId?: string; productId?: string; pcpNumber?: string; activeIngredients?: string; kind?: InputKind | '' } | null
  stock: StockItem[]
  /** Fertilizer and blend names used before on this farm. */
  pastBlends: string[]
  onName: (name: string) => void
  onPick: (input: PickedInput) => void
}) {
  const [all, setAll] = useState(false)
  const [used, setUsed] = useState(false)
  const linked = !!picked?.stockItemId || !!picked?.productId
  const registry = useRegistrySearch(name, { all, active: used && !linked })

  const inStock: PickerOption[] = stock.map((item) => ({
    id: `stock:${item.id}`,
    label: item.name,
    detail: [STOCK_LABEL[item.category], item.product && `PCP ${item.product.pcpNumber}`, `${item.onHandShown.value} ${item.onHandShown.unit} on hand`].filter(Boolean).join(' · '),
    badge: 'Stock',
  }))
  const stockNames = new Set(stock.map((s) => s.name.toLowerCase()))
  const blends: PickerOption[] = pastBlends.filter((b) => !stockNames.has(b.toLowerCase())).map((b) => ({ id: `blend:${b}`, label: b, detail: 'Fertilizer' }))
  const stockItem = stock.find((s) => s.id === picked?.stockItemId)

  function pick(option: PickerOption) {
    if (option.id.startsWith('stock:')) {
      const item = stock.find((s) => `stock:${s.id}` === option.id)!
      onPick({
        productName: item.name,
        kind: STOCK_KIND[item.category],
        stockItemId: item.id,
        productId: item.pesticideProductId ?? '',
        pcpNumber: item.product?.pcpNumber ?? '',
        activeIngredients: item.product?.activeIngredients ?? '',
      })
    } else if (option.id.startsWith('blend:')) {
      onPick({ productName: option.label, kind: 'fertilizer', stockItemId: '', productId: '', pcpNumber: '', activeIngredients: '' })
    } else {
      onPick({ ...pickedFrom(registry.results.find((r) => r.id === option.id)!), kind: 'pesticide', stockItemId: '' })
    }
  }

  return (
    <PickerInput
      value={name}
      onFocus={() => setUsed(true)}
      onChange={(text) => {
        setUsed(true)
        onName(text)
      }}
      onPick={pick}
      groups={[
        { label: 'On this farm', options: filterOptions(inStock, name) },
        { label: 'Used before', options: filterOptions(blends, name) },
        { label: 'Registered pesticides', options: registry.options },
      ]}
      linked={
        linked
          ? [stockItem ? 'Farm stock' : 'Registered product', picked?.pcpNumber && `PCP ${picked.pcpNumber}`, picked?.activeIngredients].filter(Boolean).join(' · ')
          : null
      }
      busy={registry.loading}
      listLabel="Farm stock, fertilizers used before and registered pesticides"
      emptyText={registry.failed ? 'Couldn’t search the registry. You can still type the name.' : 'No match. Keep typing the name.'}
      footerAction={{
        label: all ? 'Only current blueberry pesticides' : 'Search all registered products',
        onClick: () => {
          setUsed(true)
          setAll((a) => !a)
        },
      }}
      autoComplete="off"
    />
  )
}
